import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useMemo, useRef, useState, useCallback } from 'react';

import { UserRewindPersonFragmentFragment } from 'types/graphql-api.generated';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenFavoriteCelebrity from 'common/components/RewindModal/Konvas/ShareableScreenFavoriteCelebrity';
import Slides from 'common/components/RewindModal/Slides';
import { shardUrl } from 'common/tools/network/url';
import trans from 'common/tools/translations/trans';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';
import CelebrityPicture from './component/CelebrityPicture';

const isUserRewindPerson = (
  candidate: any
): candidate is UserRewindPersonFragmentFragment =>
  candidate?.__typename === 'Person';

type ScreenFavoriteCelebrityProps = {
  allScreens: any[];
  onNext: () => void;
  isLastStep: boolean;
  screenName: ScreenNames;
  onStep: (step: number) => void;
  step: number;
  steps: number;
  trackingScreenName?: string;
};

const ScreenFavoriteCelebrityContent = ({
  allScreens,
  onNext,
  isLastStep,
  screenName,
  isDownloadReady
}: ScreenFavoriteCelebrityProps & { isDownloadReady: boolean }) => {
  const flashbackType = useMemo(() => {
    const flashbackScreen = allScreens.find(s => s.name === 'flashback_type');
    return flashbackScreen?.data?.flashback_type;
  }, [allScreens]);

  const { celebrity, imageName } = useMemo(() => {
    if (flashbackType === 6) {
      const targetScreenName =
        screenName === 'favorite_celebrity_actor'
          ? 'screen_top_actor_2025'
          : 'screen_top_director_2025';

      const currentScreenData = allScreens.find(
        s => s.name === targetScreenName
      );
      if (!currentScreenData) return { celebrity: undefined, imageName: null };

      let foundPerson: UserRewindPersonFragmentFragment | undefined;
      if (currentScreenData.entities) {
        for (const entityList of currentScreenData.entities) {
          const person = entityList.entities?.find(isUserRewindPerson);
          if (person) {
            foundPerson = person;
            break;
          }
        }
      }
      return {
        celebrity: foundPerson,
        imageName: currentScreenData.data?.image_name
      };
    }

    const context = allScreens.some(
      s => s.name === 'screen_favorite_series_actor'
    )
      ? 'series'
      : 'movie';
    const targetScreenName =
      screenName === 'favorite_celebrity_actor'
        ? `screen_favorite_${context}_actor`
        : `screen_favorite_${context}_director`;

    const currentScreenData = allScreens.find(s => s.name === targetScreenName);
    if (!currentScreenData) return { celebrity: undefined, imageName: null };

    let foundPerson: UserRewindPersonFragmentFragment | undefined;
    if (currentScreenData.entities) {
      for (const entityList of currentScreenData.entities) {
        const person = entityList.entities?.find(isUserRewindPerson);
        if (person) {
          foundPerson = person;
          break;
        }
      }
    }
    return {
      celebrity: foundPerson,
      imageName: currentScreenData.data?.image_name
    };
  }, [screenName, allScreens]);

  const badgeUrl = useMemo(() => {
    if (!imageName) return null;
    const path = `/commons/v9/allocine/flashback/${imageName}.png`;
    return shardUrl(path);
  }, [imageName]);

  const introSubtitle = useMemo(() => {
    if (flashbackType === 6) {
      return trans('rewind.favorite.celebrities.subtitle.alternate');
    }
    return trans('rewind.favorite.celebrities.subtitle');
  }, [flashbackType]);

  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const isActor = screenName === 'favorite_celebrity_actor';
  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = isActor
          ? 'mon-acteur-allocine-2025.png'
          : 'mon-realisateur-allocine-2025.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Error handling for image generation
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading, isActor]);

  if (screenName === 'favorite_celebrity_intro') {
    const title = trans('rewind.movie_celebrities.category');
    return (
      <Slides>
        <div className={classNames(styles.contentLayout, styles.startLayout)}>
          <h2 className={styles.slideTitle}>{title}</h2>
          <h3 className={styles.slideText}>{introSubtitle}</h3>
        </div>
        <BottomActions
          showNext={!isLastStep}
          onNextClick={onNext}
          showShare={false}
        />
      </Slides>
    );
  }

  const titleForReveal = trans('rewind.movie_celebrities.category');
  const subtitleForReveal = isActor
    ? trans('rewind.favorite.celebrities.actor')
    : trans('rewind.favorite.celebrities.director');
  const celebrityFirstName = celebrity ? `${celebrity.firstName}` : null;
  const celebritylastName = celebrity ? `${celebrity.lastName}` : null;

  return (
    <>
      <Slides title={titleForReveal} subtitle={subtitleForReveal}>
        <div
          className={classNames(styles.contentLayout, styles.centeredLayout)}
        >
          <div className={styles.polaroid}>
            <div className={styles.polaroidBadge}>
              {badgeUrl && (
                <img
                  src={badgeUrl}
                  className={styles.polaroidBadgeImg}
                  alt="Badge"
                />
              )}
            </div>
            <CelebrityPicture
              celebrity={celebrity}
              imgClassName={styles.polaroidImageStar}
            />
            {celebrityFirstName && celebritylastName && (
              <h3 className={styles.celebrityName}>
                <div className={styles.celebrityFirstName}>
                  {celebrityFirstName}
                </div>{' '}
                <div className={styles.celebrityLastName}>
                  {celebritylastName}
                </div>
              </h3>
            )}
          </div>
        </div>
        <BottomActions
          showShare={true}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          shareText={
            isDownloading
              ? 'Génération...'
              : flashbackType === 6
                ? 'Partager le Top Star Allociné'
                : trans('rewind.favorite.celebrities.share')
          }
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenFavoriteCelebrity
        ref={stageRef}
        celebrity={celebrity}
        imageName={imageName}
        title={titleForReveal}
        subtitle={subtitleForReveal}
      />
    </>
  );
};

const ScreenFavoriteCelebrity = (props: ScreenFavoriteCelebrityProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenFavoriteCelebrityContent
        {...props}
        isDownloadReady={isDownloadReady}
      />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenFavoriteCelebrity;
